import { type ReactNode, useEffect, useState } from 'react';

export interface FaqItem {
  question: string;
  /** Rendered inside the accordion panel. Can be JSX for rich content. */
  answer: ReactNode;
  /** Plain-text version used for JSON-LD structured data. Defaults to `answer` if it's a string. */
  plainTextAnswer?: string;
  /**
   * Optional DOM id for deep-linking. When the page URL hash matches this id
   * (e.g. `/support#delete-account`), the item auto-opens and scrolls into view.
   */
  id?: string;
}

interface FaqAccordionProps {
  items: FaqItem[];
  /** Optional heading text. Defaults to "Frequently Asked Questions". */
  heading?: string;
  /** Optional description shown under the heading (e.g. a contact link). */
  subtitle?: ReactNode;
  /**
   * Emit `FAQPage` JSON-LD for SEO rich snippets. Defaults to `true`.
   * Set to `false` on pages that re-use Q&A from another canonical FAQ
   * page to avoid competing for the same rich result on Google.
   */
  emitJsonLd?: boolean;
  /** Tailwind max-width class for the accordion list. Defaults to `max-w-4xl`. */
  maxWidthClass?: string;
  /** Heading/subtitle text alignment. Defaults to `center`. */
  align?: 'left' | 'center';
  /**
   * Render the heading. Set false when the page already titles this
   * block, so the accordion does not repeat that title.
   */
  showHeading?: boolean;
  /**
   * Visual style. `card` (default) renders rounded, bordered rows;
   * `flat` renders the lighter, divider-separated rows from the Figma design.
   */
  variant?: 'card' | 'flat';
}

function buildFaqJsonLd(items: FaqItem[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          item.plainTextAnswer ??
          (typeof item.answer === 'string' ? item.answer : ''),
      },
    })),
  };
}

/**
 * Reusable FAQ accordion with JSON-LD `FAQPage` structured data for SEO
 * rich snippets. Answer content stays in the DOM when collapsed so crawlers
 * can index it.
 */
export const FaqAccordion = ({
  items,
  heading = 'Frequently Asked Questions',
  subtitle,
  emitJsonLd = true,
  maxWidthClass = 'max-w-4xl',
  align = 'center',
  variant = 'card',
  showHeading = true,
}: FaqAccordionProps) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  useEffect(() => {
    const openFromHash = () => {
      const hash = window.location.hash.replace('#', '');
      if (!hash) return;
      const idx = items.findIndex((item) => item.id === hash);
      if (idx === -1) return;
      setOpenIndex(idx);
      requestAnimationFrame(() => {
        document
          .getElementById(hash)
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    };
    openFromHash();
    window.addEventListener('hashchange', openFromHash);
    return () => window.removeEventListener('hashchange', openFromHash);
  }, [items]);

  useEffect(() => {
    if (!emitJsonLd) return;
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(buildFaqJsonLd(items));
    script.id = `faq-jsonld-${heading.replace(/\s+/g, '-').toLowerCase()}`;
    document.head.appendChild(script);
    return () => {
      script.remove();
    };
  }, [items, heading, emitJsonLd]);

  const isFlat = variant === 'flat';
  const alignClass = align === 'left' ? 'text-left' : 'text-center';

  const Wrapper = showHeading ? 'section' : 'div';
  return (
    <Wrapper
      className="w-full"
      {...(showHeading ? { 'aria-labelledby': 'faq-heading' } : {})}
    >
      <div className={`mx-auto ${maxWidthClass}`}>
        {showHeading && (
          <h2
            id="faq-heading"
            className={
              isFlat
                ? `mb-4 ${alignClass}
                  font-[Outfit] text-2xl font-light text-[#0d0d0d]
                  sm:text-3xl
                `
                : `mb-6 ${alignClass}
                  text-2xl font-bold text-gray-900
                  sm:mb-8 sm:text-3xl
                `
            }
          >
            {heading}
          </h2>
        )}
        {subtitle && (
          <p
            className={`
              mb-6 ${alignClass}
              font-[Outfit] text-base leading-[1.7] font-light text-[#0d0d0d]
            `}
          >
            {subtitle}
          </p>
        )}
        <div className="space-y-3">
          {items.map((item, index) => (
            <div
              key={index}
              id={item.id}
              className={
                isFlat
                  ? 'scroll-mt-24 border-b border-gray-200 bg-white'
                  : `
                    scroll-mt-24 overflow-hidden rounded-lg border
                    border-gray-200 transition-colors
                    ${openIndex === index ? 'bg-gray-50' : 'bg-white'}
                  `
              }
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                aria-expanded={openIndex === index}
                className={
                  isFlat
                    ? `
                      flex w-full cursor-pointer items-center justify-between
                      px-6 py-2.5 text-left
                    `
                    : `
                      flex w-full cursor-pointer items-center justify-between
                      px-5 py-4 text-left
                    `
                }
              >
                <span
                  className={
                    isFlat
                      ? `
                        pr-4 font-[Outfit] text-base leading-[1.7] font-light
                        text-black
                      `
                      : 'pr-4 text-sm font-medium text-gray-800 sm:text-base'
                  }
                >
                  {item.question}
                </span>
                <svg
                  className={`
                    h-5 w-5 flex-shrink-0 text-gray-500 transition-transform
                    duration-200
                    ${openIndex === index ? 'rotate-180' : ''}
                  `}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
              {/* Always in the DOM for crawlers; CSS hides when collapsed */}
              <div
                className={`overflow-hidden transition-all duration-200 ${
                  openIndex === index ? 'max-h-96 pb-4' : 'max-h-0'
                }`}
              >
                <div
                  className={
                    isFlat
                      ? `
                        px-6 font-[Outfit] text-base leading-[1.7] font-normal
                        text-black
                      `
                      : `
                        px-5 text-sm leading-relaxed text-gray-600
                        sm:text-base
                      `
                  }
                >
                  {item.answer}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Wrapper>
  );
};
